import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import { ActionButton, AppModal, InlineLoader } from '@/components/ui'
import { api, ApiError } from '@/lib/api'
import type { ScheduleResponseRow, ScheduleUserRow } from '@/types/api'
import { ScheduleEditModal } from '@/pages/schedules/ScheduleEditModal'
import {
  formatBreakLine,
  formatIntervalLine,
  pad2,
  type ScheduleEntry,
  userMonthWorkingHours,
} from '@/pages/schedules/schedule-utils'

/** Совпадает с backend `TEMPLATE_YEAR` / `TEMPLATE_MONTH` — хранилище шаблона графика. */
export const SCHEDULE_TEMPLATE_YEAR = 2030
export const SCHEDULE_TEMPLATE_MONTH = 12

const WEEK_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']

const SCHEDULE_HEAD_ROW1_PX = 36
const SCHEDULE_HEAD_ROW2_PX = 40

const scheduleTableScrollBox: CSSProperties = {
  overflow: 'auto',
  width: '100%',
  maxHeight: 'min(calc(100vh - 220px), 85vh)',
  minHeight: 200,
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain',
}

const scheduleStickyNameColBase: CSSProperties = {
  position: 'sticky',
  left: 0,
  minWidth: 160,
  maxWidth: 220,
  background: 'var(--mantine-color-body)',
  borderRight: '1px solid var(--mantine-color-default-border)',
  boxShadow: '4px 0 8px -4px rgba(0,0,0,0.12)',
}

function schedulesToByDate(rows: ScheduleResponseRow[]): Record<string, ScheduleResponseRow> {
  const m: Record<string, ScheduleResponseRow> = {}
  for (const s of rows) {
    m[String(s.date).slice(0, 10)] = s
  }
  return m
}

type DayMeta = {
  day: number
  dateStr: string
  dow: number
  isWeekend: boolean
  isToday: boolean
}

type Props = {
  opened: boolean
  onClose: () => void
  /** Право редактировать план (manage_projects + план табеля). */
  canEdit: boolean
  /** После сохранения ячейки — обновить внешний график, если нужно. */
  onSaved?: () => void | Promise<void>
}

export function ScheduleTemplateModal({ opened, onClose, canEdit, onSaved }: Props) {
  const [users, setUsers] = useState<ScheduleUserRow[]>([])
  const [allSchedules, setAllSchedules] = useState<Record<string, Record<string, ScheduleResponseRow>>>({})
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [useLocalTz, setUseLocalTz] = useState(false)
  const [editModal, setEditModal] = useState<{ userId: string; dateStr: string } | null>(null)

  const totalDays = useMemo(
    () => new Date(SCHEDULE_TEMPLATE_YEAR, SCHEDULE_TEMPLATE_MONTH, 0).getDate(),
    [],
  )

  const dayMeta = useMemo(() => {
    const rows: DayMeta[] = []
    const cal = new Date()
    const ty = cal.getFullYear()
    const tm = cal.getMonth() + 1
    const td = cal.getDate()
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${SCHEDULE_TEMPLATE_YEAR}-${pad2(SCHEDULE_TEMPLATE_MONTH)}-${pad2(day)}`
      const d = new Date(SCHEDULE_TEMPLATE_YEAR, SCHEDULE_TEMPLATE_MONTH - 1, day)
      const dow = d.getDay()
      const isWeekend = dow === 0 || dow === 6
      const isToday =
        SCHEDULE_TEMPLATE_YEAR === ty && SCHEDULE_TEMPLATE_MONTH === tm && day === td
      rows.push({ day, dateStr, dow, isWeekend, isToday })
    }
    return rows
  }, [totalDays])

  const loadAll = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const list = await api.scheduleUsers()
      const sorted = [...(Array.isArray(list) ? list : [])].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', 'ru'),
      )
      setUsers(sorted)
      const next: Record<string, Record<string, ScheduleResponseRow>> = {}
      await Promise.all(
        sorted.map(async (u) => {
          try {
            const res = await api.scheduleMonth(u.id, SCHEDULE_TEMPLATE_YEAR, SCHEDULE_TEMPLATE_MONTH)
            next[u.id] = schedulesToByDate(res.schedules)
          } catch {
            next[u.id] = {}
          }
        }),
      )
      setAllSchedules(next)
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'Не удалось загрузить шаблон')
      setUsers([])
      setAllSchedules({})
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (opened) void loadAll()
  }, [opened, loadAll])

  const stickyHeadStaff = useMemo(
    (): CSSProperties => ({
      ...scheduleStickyNameColBase,
      top: 0,
      zIndex: 6,
      verticalAlign: 'middle',
      textAlign: 'left',
      fontSize: 12,
    }),
    [],
  )

  const stickyHeadWeekday = (m: DayMeta): CSSProperties => ({
    position: 'sticky',
    top: 0,
    zIndex: 4,
    textAlign: 'center',
    fontSize: 11,
    minWidth: 56,
    height: SCHEDULE_HEAD_ROW1_PX,
    minHeight: SCHEDULE_HEAD_ROW1_PX,
    padding: '4px 2px',
    background: m.isWeekend ? 'var(--mantine-color-gray-2)' : 'var(--mantine-color-body)',
    boxShadow: 'inset 0 -1px 0 var(--mantine-color-default-border)',
  })

  const stickyHeadDayNum = (m: DayMeta): CSSProperties => ({
    position: 'sticky',
    top: SCHEDULE_HEAD_ROW1_PX,
    zIndex: 4,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 600,
    minWidth: 56,
    height: SCHEDULE_HEAD_ROW2_PX,
    minHeight: SCHEDULE_HEAD_ROW2_PX,
    padding: '4px 2px',
    background: m.isToday
      ? 'var(--mantine-color-blue-light)'
      : m.isWeekend
        ? 'var(--mantine-color-gray-2)'
        : 'var(--mantine-color-body)',
  })

  const stickyHeadHours = useMemo(
    (): CSSProperties => ({
      position: 'sticky',
      top: 0,
      zIndex: 5,
      verticalAlign: 'middle',
      textAlign: 'center',
      minWidth: 80,
      width: 80,
      background: 'var(--mantine-color-body)',
      fontSize: 12,
      boxShadow: 'inset 0 -1px 0 var(--mantine-color-default-border)',
    }),
    [],
  )

  const stickyBodyName = (idx: number): CSSProperties => ({
    ...scheduleStickyNameColBase,
    zIndex: 3,
    verticalAlign: 'top',
    background: idx % 2 === 0 ? 'var(--table-striped-color)' : 'var(--mantine-color-body)',
  })

  const openEdit = (userId: string, dateStr: string) => {
    if (!canEdit) return
    setEditModal({ userId, dateStr })
  }

  const editExisting = editModal
    ? allSchedules[editModal.userId]?.[editModal.dateStr]
    : undefined

  const renderPlanCell = (u: ScheduleUserRow, dateStr: string, meta: DayMeta) => {
    const schedule = allSchedules[u.id]?.[dateStr] as ScheduleEntry | undefined
    const line = formatIntervalLine(schedule, dateStr, useLocalTz)
    const br = formatBreakLine(schedule, dateStr, useLocalTz)
    const has = !!line
    const background = meta.isToday
      ? 'var(--mantine-color-blue-light)'
      : meta.isWeekend
        ? 'var(--mantine-color-gray-1)'
        : undefined
    return (
      <Table.Td
        key={dateStr}
        style={{
          background,
          fontSize: 11,
          verticalAlign: 'top',
          minWidth: 72,
          maxWidth: 120,
          cursor: canEdit ? 'pointer' : 'default',
        }}
        onClick={() => openEdit(u.id, dateStr)}
      >
        {has ? (
          <Stack gap={2}>
            <Text size="xs" lh={1.3}>
              {line}
            </Text>
            {br ? (
              <Text size="xs" c="dimmed" lh={1.2}>
                п: {br}
              </Text>
            ) : null}
          </Stack>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
    )
  }

  const afterSave = async () => {
    await loadAll()
    await onSaved?.()
  }

  return (
    <>
      <AppModal
        opened={opened}
        onClose={onClose}
        title={
          <Stack gap={2}>
            <Text fw={600}>Шаблон графика</Text>
            <Text size="sm" c="dimmed" fw={400}>
              Отдельное хранилище (декабрь {SCHEDULE_TEMPLATE_YEAR}): сюда попадает график по кнопке «Сделать
              шаблоном», отсюда — «Применить шаблон» к выбранному месяцу на странице табеля.
            </Text>
          </Stack>
        }
        size="100%"
        fullScreen
        footer={
          <Group justify="space-between" w="100%" wrap="wrap">
            <Checkbox
              label="По моему времени"
              checked={useLocalTz}
              onChange={(e) => setUseLocalTz(e.currentTarget.checked)}
            />
            <Group gap="sm">
              <Button
                leftSection={<IconRefresh size={16} />}
                variant="default"
                onClick={() => void loadAll()}
                disabled={loading}
              >
                Обновить
              </Button>
              <ActionButton action="close" onClick={onClose} />
            </Group>
          </Group>
        }
      >
        <Stack gap="sm">
          {loadError ? (
            <Alert color="red" title="Ошибка" onClose={() => setLoadError(null)} withCloseButton>
              {loadError}
            </Alert>
          ) : null}
          {loading ? (
            <InlineLoader label="Загрузка шаблона…" />
          ) : users.length === 0 ? (
            <Text size="sm" c="dimmed">
              Нет сотрудников в графике или пустой список.
            </Text>
          ) : (
            <Box style={scheduleTableScrollBox}>
              <Table
                striped
                highlightOnHover
                withTableBorder
                style={{
                  minWidth: 720,
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                }}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th rowSpan={2} style={stickyHeadStaff}>
                      Сотрудник
                    </Table.Th>
                    {dayMeta.map((m) => (
                      <Table.Th key={m.dateStr} style={stickyHeadWeekday(m)}>
                        <div>{WEEK_SHORT[m.dow]}</div>
                      </Table.Th>
                    ))}
                    <Table.Th rowSpan={2} style={stickyHeadHours}>
                      Часы
                    </Table.Th>
                  </Table.Tr>
                  <Table.Tr>
                    {dayMeta.map((m) => (
                      <Table.Th key={`d-${m.dateStr}`} style={stickyHeadDayNum(m)}>
                        {m.day}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {users.map((u, idx) => {
                    const totalPlanH = userMonthWorkingHours(
                      u.id,
                      SCHEDULE_TEMPLATE_YEAR,
                      SCHEDULE_TEMPLATE_MONTH,
                      allSchedules as unknown as Record<string, Record<string, ScheduleEntry>>,
                    )
                    return (
                      <Table.Tr key={u.id}>
                        <Table.Td style={stickyBodyName(idx)}>
                          <Text size="sm" fw={500} lineClamp={2}>
                            {u.name}
                          </Text>
                        </Table.Td>
                        {dayMeta.map((meta) => renderPlanCell(u, meta.dateStr, meta))}
                        <Table.Td style={{ fontSize: 12, verticalAlign: 'middle', minWidth: 80 }}>
                          <Text size="sm">
                            {totalPlanH > 0
                              ? totalPlanH % 1 === 0
                                ? `${totalPlanH} ч`
                                : `${totalPlanH.toFixed(1).replace('.', ',')} ч`
                              : '—'}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            </Box>
          )}
        </Stack>
      </AppModal>

      {editModal && (
        <ScheduleEditModal
          opened
          onClose={() => setEditModal(null)}
          userId={editModal.userId}
          userName={users.find((x) => x.id === editModal.userId)?.name || ''}
          dateStr={editModal.dateStr}
          year={SCHEDULE_TEMPLATE_YEAR}
          month={SCHEDULE_TEMPLATE_MONTH}
          existing={editExisting}
          onReload={afterSave}
        />
      )}
    </>
  )
}
